<script>
  import { createEventDispatcher, onMount, onDestroy } from 'svelte';
  import * as d3 from 'd3';
  import { capacity, capacityColor, capacityTextColor } from '../stores.js';
  import TrendIndicator from './TrendIndicator.svelte';

  export let data = [];
  export let value = '--';
  export let capacityLevel = 0;
  export let size = 160;
  export let trend = null;
  export let previousCapacityLevel = null;
  export let showTrend = false;

  let canvas;
  let mounted = false;
  const dispatch = createEventDispatcher();

  const allowedTrends = ['na', 'up', 'down', 'flat'];
  const trendLabels = {
    na: 'No comparable data',
    up: 'Increase in capacity',
    down: 'Reduction in capacity',
    flat: 'No change'
  };

  function numericSize(size) {
    return typeof size === 'number' ? size : parseFloat(size) || 160;
  }

  function normalizeData(data) {
    if (!data) return [];
    return Array.isArray(data) ? data : Object.values(data);
  }

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function draw() {
    const chartData = normalizeData(data);

    if (!mounted || !canvas || !d3 || !chartData.length) return;

    const width = numericSize(size);
    const height = numericSize(size);
    const radius = width / 2;
    const innerRadius = radius * 0.83;

    const element = d3.select(canvas);
    element.selectAll('*').remove();

    const svg = element.append('svg').attr('width', width).attr('height', height);
    const mainGroup = svg
      .append('g')
      .attr('transform', `translate(${width / 2}, ${height / 2})`);

    const pieData = d3.pie().sort(null)(chartData);

    mainGroup
      .selectAll('path')
      .data(pieData)
      .enter()
      .append('path')
      .attr('fill', (d, index) => capacityColor(index + 1))
      .attr('d', d3.arc().innerRadius(innerRadius).outerRadius(radius))
      .attr('data-capacity', (d, index) => index + 1)
      .on('mouseenter', function (event) {
        dispatch('capacityhover', {
          capacity: Number(this.getAttribute('data-capacity')) || 0,
          sourceEvent: event
        });
      })
      .on('mousemove', function (event) {
        dispatch('capacitymove', {
          capacity: Number(this.getAttribute('data-capacity')) || 0,
          sourceEvent: event
        });
      });

    svg.on('mouseleave', () => {
      dispatch('capacityleave');
    });
  }

  onMount(() => {
    mounted = true;
    draw();
  });

  onDestroy(() => {
    if (canvas) {
      d3.select(canvas).selectAll('*').remove();
    }
  });
  $: resolvedCapacity = clamp(Math.ceil(Number(capacityLevel) || 0), 0, 5);
  $: resolvedPreviousCapacity = previousCapacityLevel == null
    ? resolvedCapacity
    : clamp(Math.ceil(Number(previousCapacityLevel) || 0), 0, 5);
  $: calculatedTrend = !previousCapacityLevel
    ?'na' 
    :capacityLevel > previousCapacityLevel
    ? 'up'
    : capacityLevel < previousCapacityLevel
    ? 'down'
    : 'flat';
  $: resolvedTrend = allowedTrends.includes(trend) ? trend : calculatedTrend;
  $: resolvedTrendLabel = trendLabels[resolvedTrend];
  $: resolvedCapacityLabel = capacity(resolvedCapacity);
  $: resolvedCapacityTextColor = capacityTextColor(resolvedCapacity);

  $: if (mounted && data && value !== undefined && capacityLevel !== undefined && size) {
    draw();
  }
</script>

<div
  class="donut-chart"
  style="width: {numericSize(size)}px; height: {numericSize(size)}px;"
>
  <div bind:this={canvas} class="donut-chart__canvas"></div>

  <div class="donut-chart__center">
    <p class="donut-chart__capacity-label" style="color: {resolvedCapacityTextColor};">
      {resolvedCapacityLabel}
    </p>

  {#if showTrend }
    <div class="donut-chart__trend" data-trend={resolvedTrend}>
      <TrendIndicator trend={resolvedTrend} size="cell" />
      <p class="donut-chart__trend-label">{resolvedTrendLabel}</p>
    </div>
  {/if}

  </div>
</div>
